import { useState, useEffect } from 'react';
import api from '../../services/api';
import type { CallSummary } from '../RunProgressPage/runProgressTypes';

export interface LeadData {
  id: string;
  name: string;
  phone: string;
  email?: string;
  run_id?: string | null;
  call_status: string;
  current_disposition?: string;
  current_temperature?: string | null;
  last_contact_at?: string;
  total_attempts?: number;
  created_at: string;
  retry_count: number;
}

export interface CampaignInfo {
  run_id: string;
  name: string;
}

export function useLeadDetailPage(leadId: string | undefined) {
  const [lead, setLead] = useState<LeadData | null>(null);
  const [callHistory, setCallHistory] = useState<CallSummary[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [leadsRes, historyRes] = await Promise.all([
          api.get('/leads/'),
          api.get(`/leads/${leadId}/history`),
        ]);

        if (cancelled) return;

        const allLeads: LeadData[] = leadsRes.data ?? [];
        const matchedLead = allLeads.find((l) => l.id === leadId) ?? null;
        setLead(matchedLead);

        const history: CallSummary[] = historyRes.data ?? [];
        setCallHistory(history);

        if (matchedLead) {
          try {
            // `/runs/` defaults to a 30-day window server-side; widen it so we
            // can match this lead's run_id even when the run is older.
            const runsRes = await api.get('/runs/', { params: { days: 10000, limit: 1000 } });
            const allRuns: any[] = runsRes.data ?? [];
            const leadRunIds = new Set<string>();
            if (matchedLead.run_id) leadRunIds.add(matchedLead.run_id);

            const matchedCampaigns: CampaignInfo[] = [];
            for (const run of allRuns) {
              if (leadRunIds.has(run.id)) {
                matchedCampaigns.push({ run_id: run.id, name: run.name });
              }
            }

            if (matchedCampaigns.length === 0 && matchedLead.run_id) {
              const runMatch = allRuns.find((r) => r.id === matchedLead.run_id);
              if (runMatch) {
                matchedCampaigns.push({ run_id: runMatch.id, name: runMatch.name });
              }
            }

            setCampaigns(matchedCampaigns);
          } catch {
            setCampaigns([]);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load lead details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [leadId]);

  return { lead, callHistory, campaigns, loading, error };
}
